import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { agentDataMap } from '../data/agentDetailData';

import AgentDetailCard from '../components/AgentDetailCard';
import WorkflowRunner from '../components/WorkflowRunner';
import RightSlider from '../components/RightSlider';
import VideoModal from '../components/VideoModal'; // Import the VideoModal

import './AgentDetailPage.css';

// Your back button styles from before
const backButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 1.25rem',
  fontSize: '1rem',
  fontWeight: 500,
  color: '#fff',
  background: 'linear-gradient(90deg, #4f8cff 0%, #2356c7 100%)',
  border: 'none',
  borderRadius: '2rem',
  boxShadow: '0 2px 8px rgba(79,140,255,0.12)',
  cursor: 'pointer',
  transition: 'background 0.2s, box-shadow 0.2s',
  marginBottom: '1.5rem',
};
const backButtonHoverStyle = {
  background: 'linear-gradient(90deg, #2356c7 0%, #4f8cff 100%)',
  boxShadow: '0 4px 16px rgba(79,140,255,0.18)',
};

// Helper component for rendering content
const Content = ({ data }) => {
    if (typeof data === 'string') {
        return <p>{data}</p>;
    }
    if (Array.isArray(data)) {
        return <ul className="detail-list">{data.map((item, i) => <li key={i}>{item}</li>)}</ul>;
    }
    if (typeof data === 'object' && data !== null) {
        return (
            <>
                {data.description && <p>{data.description}</p>}
                {data.points && <ul className="detail-list">{data.points.map((item, i) => <li key={i}>{item}</li>)}</ul>}
            </>
        );
    }
    return null;
};

const AgentUseCasePage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const agentData = agentDataMap[agentId] || agentDataMap['default'];

  // State for the back button hover
  const [isBackHovered, setIsBackHovered] = useState(false);

  // --- NEW: State for video preview and modal ---
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isVideoEnabled = !!agentData.videoPath;
  
  // Data for the right slider
  const benefitsData = agentData.details?.find(d => d.title.includes("Business Value"))?.points || agentData.businessContext?.businessOutcomes || [];
  const tierModelData = agentData.agenticModel || {};
  const kpiData = agentData.kpis || [];
  const solutionFlowData = agentData.solutionFlowData || [];

  return (
    <div className="agent-detail-page">
      <button
        className="back-button"
        style={isBackHovered ? { ...backButtonStyle, ...backButtonHoverStyle } : backButtonStyle}
        onClick={() => navigate(-1)}
        onMouseEnter={() => setIsBackHovered(true)}
        onMouseLeave={() => setIsBackHovered(false)}
        aria-label="Go back"
      >
        <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: 6}}><path d="M12.5 16L7.5 10L12.5 4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        Back
      </button>

      {/* --- NEW: Header with Video Icon --- */}
      <div className="agent-page-header">
        <h1>{agentData.title}</h1>
        <div 
          className={`video-icon-wrapper-page ${!isVideoEnabled ? 'disabled' : ''}`}
          onMouseEnter={() => isVideoEnabled && setIsPreviewing(true)}
          onMouseLeave={() => isVideoEnabled && setIsPreviewing(false)}
        >
          <button onClick={() => isVideoEnabled && setIsModalOpen(true)} title="Watch Video">
            <i className="fas fa-video"></i>
          </button>
          {isPreviewing && isVideoEnabled && (
            <div className="video-preview-box-page">
              <video src={agentData.videoPath} autoPlay muted loop playsInline />
            </div>
          )}
        </div>
      </div>

      {/* Conditionally render the Video Modal */}
      {isModalOpen && <VideoModal videoPath={agentData.videoPath} onClose={() => setIsModalOpen(false)} />}

       {agentData.workflowImagePath && (
        <div className="workflow-image-section">
          <h3>Workflow Visualization</h3>
          <div className="image-container">
            <img src={agentData.workflowImagePath} alt={`${agentData.title} Workflow`} />
          </div>
        </div>
      )}
        {/* --- RENDER DETAILED OR GENERIC LAYOUT --- */}
        {agentData.sections ? (
            <div className="structured-view-content">
                {agentData.sections.map(section => (
                    <section key={section.title} className="detail-section">
                        <h2>{section.title}</h2>
                        {section.subSections.map(sub => (
                            <div key={sub.title || Math.random()} className="detail-subsection">
                                {sub.title && <h3>{sub.title}</h3>}
                                <Content data={sub.content} />
                            </div>
                        ))}
                    </section>
                ))}
            </div>
        ) : (
            <div className="generic-view">
                <p className="generic-view-description">{agentData.description}</p>
                <div className="card-grid">
                    {agentData.details
                        ?.filter(detail => !detail.title.includes("Business Value"))
                        .map(detail => (
                            <AgentDetailCard key={detail.title} title={detail.title} points={detail.points} />
                    ))}
                </div>
            </div>
        )}

        <WorkflowRunner 
            agentName={agentData.title} 
            webhookUrl={agentData.webhookUrl} 
        />
        <RightSlider 
            benefitsContent={benefitsData} 
            tierModelContent={tierModelData} 
            kpiContent={kpiData}
            solutionFlowData={solutionFlowData}
        />
    </div>
  );
};

// You can move the Content helper outside if you prefer
// const Content = ({ data }) => {
//     if (typeof data === 'string') return <p>{data}</p>;
//     if (Array.isArray(data)) return <ul className="detail-list">{data.map((item, i) => <li key={i}>{item}</li>)}</ul>;
//     if (typeof data === 'object' && data !== null) {
//         return (
//             <>
//                 {data.description && <p>{data.description}</p>}
//                 {data.points && <ul className="detail-list">{data.points.map((item, i) => <li key={i}>{item}</li>)}</ul>}
//             </>
//         );
//     }
//     return null;
// };


export default AgentUseCasePage;